import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  AlertTriangle,
  Wrench,
  User,
  Calendar,
  DollarSign,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Laptop,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import api from '@/services/api';

// Repair status color mapping
function getRepairStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    IN_TRANSIT: 'bg-blue-100 text-blue-800',
    AT_PROVIDER: 'bg-purple-100 text-purple-800',
    IN_REPAIR: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-green-100 text-green-800',
    WRITE_OFF: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getRepairTypeColor(type: string): string {
  const colors: Record<string, string> = {
    CORRECTIVE: 'bg-red-50 text-red-700',
    PREVENTIVE: 'bg-blue-50 text-blue-700',
    WARRANTY: 'bg-green-50 text-green-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

export default function EmployeeRepairHistoryPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));

  // Update URL params when page changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params);
  }, [page, setSearchParams]);

  // Fetch employee details
  const { data: employee, isLoading: loadingEmployee } = useQuery({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}`);
      return response.data.data;
    },
    enabled: Boolean(id),
  });

  // Fetch repair history
  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['employeeRepairHistory', id, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      const response = await api.get(`/repairs/employee/${id}/history?${params.toString()}`);
      return response.data;
    },
    enabled: Boolean(id),
  });

  const repairs = historyData?.data || [];
  const pagination = historyData?.pagination;
  const stats = historyData?.stats;

  const isLoading = loadingEmployee || loadingHistory;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
        <p className="text-lg font-medium">Employee not found</p>
        <p className="text-muted-foreground mb-4">The employee you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/repairs">Back to Repairs</Link>
        </Button>
      </div>
    );
  }

  // Calculate risk level based on repair history
  const getRiskLevel = () => {
    if (!stats) return { level: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    
    const totalRepairs = stats.totalRepairs || 0;
    const correctiveRepairs = stats.correctiveRepairs || 0;
    const totalCost = stats.totalCost || 0;

    if (totalRepairs >= 5 || correctiveRepairs >= 3 || totalCost >= 5000) {
      return { level: 'High Risk', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    } else if (totalRepairs >= 3 || correctiveRepairs >= 2 || totalCost >= 2000) {
      return { level: 'Medium Risk', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle };
    } else {
      return { level: 'Low Risk', color: 'bg-green-100 text-green-800', icon: null };
    }
  };

  const risk = getRiskLevel();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/repairs">
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Repair History</h1>
            <span className={cn(
              "px-3 py-1 text-sm font-medium rounded-full flex items-center gap-1",
              risk.color
            )}>
              {risk.icon && <risk.icon className="w-4 h-4" />}
              {risk.level}
            </span>
          </div>
          <p className="text-muted-foreground">
            Track record for {employee.firstName} {employee.lastName}
          </p>
        </div>
      </div>

      {/* Employee Info & Stats */}
      <div className="grid gap-6 lg:grid-cols-4">
        {/* Employee Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-4 h-4" />
              Employee
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-lg">{employee.firstName} {employee.lastName}</p>
                <p className="text-sm text-muted-foreground">{employee.employeeId}</p>
              </div>
              {employee.email && (
                <p className="text-sm text-muted-foreground">{employee.email}</p>
              )}
              {employee.department && (
                <p className="text-sm">{employee.department.name}</p>
              )}
              {employee.jobTitle && (
                <p className="text-sm text-muted-foreground">{employee.jobTitle}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Total Repairs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wrench className="w-4 h-4" />
              Total Repairs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{stats?.totalRepairs || 0}</p>
            <div className="flex gap-2 mt-2 text-sm">
              <span className="text-red-600">{stats?.correctiveRepairs || 0} corrective</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-blue-600">{stats?.preventiveRepairs || 0} preventive</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Cost */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="w-4 h-4" />
              Total Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">
              {stats?.totalCost ? formatCurrency(stats.totalCost) : '$0'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Avg: {stats?.averageCost ? formatCurrency(stats.averageCost) : '$0'} per repair
            </p>
          </CardContent>
        </Card>

        {/* Assets Damaged */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Laptop className="w-4 h-4" />
              Assets Affected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{stats?.assetsAffected || 0}</p>
            <div className="text-sm mt-2">
              {stats?.writeOffs > 0 && (
                <span className="text-red-600 font-medium">
                  {stats.writeOffs} write-off{stats.writeOffs > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Assessment */}
      {(stats?.totalRepairs >= 3 || stats?.correctiveRepairs >= 2) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Employee Damage Assessment</p>
              <p className="text-sm text-yellow-700 mt-1">
                This employee has a history of {stats.totalRepairs} repair{stats.totalRepairs > 1 ? 's' : ''}, 
                including {stats.correctiveRepairs} corrective (damage-related) repair{stats.correctiveRepairs > 1 ? 's' : ''}.
                {stats.totalCost > 0 && ` Total repair costs: ${formatCurrency(stats.totalCost)}.`}
                {stats.writeOffs > 0 && ` ${stats.writeOffs} asset(s) had to be written off.`}
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                Consider reviewing asset handling guidelines with this employee.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Repair History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Repair History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {repairs.length === 0 ? (
            <div className="text-center py-12">
              <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No repair history</p>
              <p className="text-muted-foreground">
                This employee has no recorded repairs.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Repair #</th>
                    <th className="text-left p-4 font-medium">Asset</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Issue</th>
                    <th className="text-left p-4 font-medium">Cost</th>
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {repairs.map((repair: any) => (
                    <tr key={repair.id} className="hover:bg-muted/50 transition-colors">
                      <td className="p-4">
                        <Link 
                          to={`/repairs/${repair.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {repair.repairNumber}
                        </Link>
                      </td>
                      <td className="p-4">
                        {repair.asset ? (
                          <div>
                            <p className="font-medium text-sm">{repair.asset.assetTag}</p>
                            <p className="text-xs text-muted-foreground">{repair.asset.name}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getRepairTypeColor(repair.repairType)
                        )}>
                          {repair.repairType}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getRepairStatusColor(repair.status)
                        )}>
                          {repair.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm line-clamp-2 max-w-[250px]">
                          {repair.issueDescription || '-'}
                        </p>
                      </td>
                      <td className="p-4 text-sm">
                        {repair.actualCost 
                          ? formatCurrency(repair.actualCost)
                          : repair.estimatedCost 
                            ? <span className="text-muted-foreground">~{formatCurrency(repair.estimatedCost)}</span>
                            : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-muted-foreground" />
                          {formatDate(repair.createdAt)}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/repairs/${repair.id}`}>View</Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} repairs
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
